/**
 * Desktop Table View for Side-by-Side Scores Matrix
 * Shows all models with Overall score and 7 criteria in a compressed table layout
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { CompositeScore } from '../../../hooks/useComparisonScores';
import { getModelLogo } from '../../../utils/logoMapping';
import { formatModelDisplayName } from '../../../utils/modelDisplayName';
import { ScoringRubricModal } from './ScoringRubricModal';

interface SideBySideMatrixDesktopProps {
  scores: CompositeScore[];
  onOpenResponse: (modelId: string) => void;
}

// 7 Criteria from user-facing rubric with icons and descriptions
const CRITERIA_CONFIG = [
  { name: 'Accuracy', dbName: 'Accuracy', icon: '🎯', description: 'Facts, math, and claims are correct and consistent.' },
  { name: 'Completeness', dbName: 'Completeness', icon: '✅', description: 'Answers every required part in the requested format.' },
  { name: 'Clarity', dbName: 'Clarity', icon: '📘', description: 'Easy to follow; clear structure and labeling.' },
  { name: 'Relevance', dbName: 'Relevance', icon: '🔍', description: 'Stays on-topic; follows your instructions and constraints.' },
  { name: 'Creativity', dbName: 'Creativity', icon: '💡', description: 'Useful, original ideas—no boilerplate or fluff.' },
  { name: 'Bias & Safety', dbName: 'Balance', icon: '🛡️', description: 'Responsible tone; avoids unsafe or policy-breaking content.' },
  { name: 'Conciseness', dbName: 'Conciseness', icon: '✂️', description: 'Gets to the point; just enough detail to be useful.' }
];

export const SideBySideMatrixDesktop: React.FC<SideBySideMatrixDesktopProps> = ({
  scores,
  onOpenResponse
}) => {
  const [activeCriterion, setActiveCriterion] = useState<typeof CRITERIA_CONFIG[0] | null>(null);
  const [showRubricModal, setShowRubricModal] = useState(false);
  const lastTriggerRef = useRef<HTMLButtonElement | null>(null);

  const closeCriterionModal = useCallback(() => {
    setActiveCriterion(null);
    if (lastTriggerRef.current) {
      lastTriggerRef.current.focus();
      lastTriggerRef.current = null;
    }
  }, [setActiveCriterion]);

  useEffect(() => {
    if (!activeCriterion) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeCriterionModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeCriterion, closeCriterionModal]);

  // Helper to get score for a specific criterion
  const getCriterionScore = (score: CompositeScore, dbName: string): number => {
    const criterionScore = score.criterionScores.find(cs =>
      cs.criterionName.toLowerCase() === dbName.toLowerCase()
    );
    return criterionScore?.score ?? 0;
  };

  const getCriterionColorClass = (score: number): string => {
    if (score >= 9) return 'text-emerald-400 font-semibold';
    if (score >= 8) return 'text-green-400 font-medium';
    if (score >= 7) return 'text-yellow-400';
    if (score >= 6) return 'text-orange-400';
    return 'text-red-400';
  };

  // Find highest overall score for gold highlighting
  const highestScore = Math.max(...scores.map(s => s.compositeScore ?? 0), 0);

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          {/* Sticky Header */}
          <thead className="sticky top-0 bg-slate-900 z-10 border-b border-slate-700">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider sticky left-0 bg-slate-900 border-r border-slate-700/50">
                Model
              </th>
              <th className="px-2 py-2 text-center text-xs font-semibold text-slate-400 uppercase tracking-wider min-w-[60px]">
                Overall
              </th>
              {CRITERIA_CONFIG.map(criterion => (
                <th
                  key={criterion.dbName}
                  className="px-2 py-2 text-center min-w-[70px]"
                >
                  <button
                    onClick={(event) => {
                      lastTriggerRef.current = event.currentTarget;
                      setActiveCriterion(criterion);
                    }}
                    className="flex flex-col items-center gap-0.5 w-full text-xs hover:text-cyan-400 transition-colors group"
                    title={`Click to learn about ${criterion.name}`}
                    type="button"
                  >
                    <span className="text-base group-hover:scale-110 transition-transform">{criterion.icon}</span>
                    <span className="font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap text-[10px]">
                      {criterion.name.split(' ')[0]}
                    </span>
                  </button>
                </th>
              ))}
            </tr>
          </thead>

          {/* Table Body */}
          <tbody>
            {scores
              .filter(s => s.compositeScore != null && s.compositeScore > 0)
              .sort((a, b) => (b.compositeScore ?? 0) - (a.compositeScore ?? 0))
              .map((score, index) => {
                const isHighestScore = score.compositeScore === highestScore && (score.compositeScore ?? 0) > 0;

                return (
                  <tr
                    key={score.modelId}
                    onClick={() => onOpenResponse(score.modelId)}
                    className={`border-b border-slate-700/50 hover:bg-slate-800/50 cursor-pointer transition-colors ${
                      isHighestScore ? 'bg-yellow-500/5 hover:bg-yellow-500/10' : ''
                    }`}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onOpenResponse(score.modelId);
                      }
                    }}
                    aria-label={`View ${score.modelDisplayName || score.modelName} response`}
                  >
                    {/* Model Name + Logo */}
                    <td className="px-3 py-2 sticky left-0 bg-slate-900 border-r border-slate-700/50">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-slate-700/30 rounded flex items-center justify-center p-0.5 flex-shrink-0">
                          <img
                            src={getModelLogo(score.modelId, score.modelName)}
                            alt={`${score.modelName} logo`}
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="font-medium text-white text-sm truncate">
                            {score.modelDisplayName || formatModelDisplayName(score.modelId)}
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* Overall Score */}
                    <td className="px-2 py-2 text-center">
                      <span className={`text-sm ${getCriterionColorClass(score.compositeScore ?? 0)}`}>
                        {(score.compositeScore ?? 0) > 0 ? (score.compositeScore ?? 0).toFixed(1) : '—'}
                      </span>
                    </td>

                    {/* Criteria Scores */}
                    {CRITERIA_CONFIG.map(criterion => {
                      const criterionScore = getCriterionScore(score, criterion.dbName);
                      return (
                        <td key={criterion.dbName} className="px-2 py-2 text-center">
                          <span className={`text-sm ${getCriterionColorClass(criterionScore)}`}>
                            {criterionScore > 0 ? criterionScore.toFixed(1) : '—'}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Criterion Description Popup */}
      {activeCriterion && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
          onClick={closeCriterionModal}
          role="dialog"
          aria-modal="true"
          aria-label={`${activeCriterion.name} definition`}
        >
          <div
            className="bg-slate-800 rounded-lg p-6 max-w-md mx-4 border border-slate-700 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-3">
                <span className="text-4xl">{activeCriterion.icon}</span>
                <h3 className="text-xl font-bold text-white">{activeCriterion.name}</h3>
              </div>
              <button
                type="button"
                onClick={closeCriterionModal}
                aria-label="Close"
                className="text-slate-400 hover:text-white transition-colors p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
              >
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M3.5 3.5l9 9m0-9-9 9"
                    stroke="currentColor"
                    strokeWidth={1.5}
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
            <p className="text-slate-300 leading-relaxed">{activeCriterion.description}</p>
          </div>
        </div>
      )}

      {/* Scoring Rubric Modal */}
      <ScoringRubricModal
        isOpen={showRubricModal}
        onClose={() => setShowRubricModal(false)}
      />
    </>
  );
};
